import { useCallback, useEffect, useState } from 'react';
import {
  CONTROL_PANEL_PATHS,
  operatorAgentSpendResponseSchema,
  operatorOperationsResponseSchema,
  operatorRosterResponseSchema,
  operatorTenantListResponseSchema,
} from '@forecastify/shared';
import { useOperatorSession } from './session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C2 · What needs somebody right now, read once and shown in two places.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SIDEBAR BADGES AND THE HEALTH TILES ARE THE SAME FOUR NUMBERS, SO THEY ARE READ
 * ONCE.** The redesign puts a count beside three nav rows and a tile for each on the landing
 * screen, which is two renderings of one question — *is anything wrong?* Two fetches would be two
 * answers, and the interesting case is exactly when they disagree: a badge saying 3 above a strip
 * saying 2 is worse than either number alone, because then nobody trusts the console.
 *
 * ⚠️⚠️ **NO `GET /control-panel/health` ENDPOINT, AND THAT IS A DELIBERATE *NOT YET*.** Three reads
 * on first paint is more than one would be, and one would mean a new endpoint whose job is to
 * re-aggregate what three existing endpoints already return correctly. The three are cheap — the
 * operations one is counts, and the tenant list is the landing screen's own data either way — so
 * the endpoint is worth adding when somebody measures it being slow, with the measurement, rather
 * than now on the guess that it will be.
 *
 * ⚠️ **It never reports a partial answer as a whole one.** A console that silently shows three of
 * four tiles is a console saying *nothing is wrong* about the thing it failed to read. Any read
 * failing puts the whole hook in `failed`, and the strip says so.
 */

/** One thing that wants attention, named so a tile can list it rather than only count it. */
export interface HealthItem {
  /** What a reader would call it — a chore's description, a job kind, a workspace, a slug. */
  readonly label: string;
  /**
   * ⚠️ **How many this item stands for**, where that is not one. A job KIND with three failures is
   * one entry in the tile's subtitle and three in its number — the mockup's *Hibás háttérfeladat 3*
   * over *email.send, export.render, webhook.deliver* is exactly that split.
   */
  readonly count?: number;
  /** For a late chore: how many of its cadences have gone by without it. */
  readonly missed?: number;
}

export interface Health {
  /** Chores that have missed two cadences. `overdue` is the server's own judgement, not ours. */
  readonly overdue: readonly HealthItem[];
  /** Job kinds with at least one failure. Kinds, not rows: the queue reports per kind. */
  readonly failedJobs: readonly HealthItem[];
  /** Workspaces that have reached the daily token ceiling. Empty when no cap is configured. */
  readonly atCeiling: readonly HealthItem[];
  /** Suspended organisations. */
  readonly suspended: readonly HealthItem[];
  /**
   * ⚠️ C8 · operators invited and not yet enrolled — the sidebar's *Operátorok* badge. They cannot
   * sign in until they do, so the number is a list of people somebody is waiting on.
   */
  readonly unenrolledOperators: number;
}

export type HealthState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'ready'; readonly health: Health };

/** How many of the four lists have something in them. The strip's one-line summary counts these. */
/**
 * ⚠️⚠️ **THE THREE THINGS SOMEBODY HAS TO GO AND DO — NOT THE FOUR TILES.** The mockup's
 * *"6 tétel vár rád"* is 1 + 3 + 2: late work, failed jobs and workspaces at the ceiling. A
 * suspended organisation is a decision somebody already made, not a task waiting for one, and
 * counting it would make the headline grow every time an operator does their job.
 */
export function problemCount(health: Health): number {
  return health.overdue.length + failedJobCount(health) + health.atCeiling.length;
}

/** Failed JOBS, not failed kinds — the number the tile prints. */
export function failedJobCount(health: Health): number {
  return health.failedJobs.reduce((sum, item) => sum + (item.count ?? 1), 0);
}

export function useHealth(): HealthState {
  const session = useOperatorSession();
  const [state, setState] = useState<HealthState>({ kind: 'loading' });

  const load = useCallback(async (): Promise<HealthState> => {
    /**
     * ⚠️ **`Promise.all`, not three awaits.** They are independent reads and nothing can draw
     * until all four numbers exist, so serialising them makes the landing screen slower by the
     * sum rather than by the maximum.
     */
    const [operations, spend, tenants, operators] = await Promise.all([
      session.request<unknown>(CONTROL_PANEL_PATHS.operations),
      /**
       * ⚠️⚠️ **ONE DAY, BECAUSE THE CEILING IS A DAILY ONE.** The agent cap resets at the UTC day
       * boundary, so a workspace that spent its allowance last Tuesday is not at the ceiling now —
       * and asking for thirty days would report it as though it were. The tile's own wording is
       * "at the ceiling", present tense.
       */
      session.request<unknown>(CONTROL_PANEL_PATHS.agentSpend(1)),
      session.request<unknown>(CONTROL_PANEL_PATHS.tenants),
      session.request<unknown>(CONTROL_PANEL_PATHS.operators),
    ]);

    if (!operations.ok || !spend.ok || !tenants.ok) return { kind: 'failed' };

    /* ⚠️ The roster is a badge, not a tile: if it fails the badge is absent rather than the whole
       strip reporting a failure about something it does not show. */
    const roster = operators.ok ? operatorRosterResponseSchema.safeParse(operators.value) : null;
    const unenrolledOperators =
      roster?.success === true
        ? roster.data.data.filter((row) => row.status === 'active' && !row.enrolled).length
        : 0;

    /* ⚠️ A 200 that fails the contract is a failure, not silence — the same rule `operations.tsx`
       states: read through, and this draws an empty strip, which says "nothing is wrong". */
    const ops = operatorOperationsResponseSchema.safeParse(operations.value);
    const usage = operatorAgentSpendResponseSchema.safeParse(spend.value);
    const orgs = operatorTenantListResponseSchema.safeParse(tenants.value);
    if (!ops.success || !usage.success || !orgs.success) return { kind: 'failed' };

    /**
     * ⚠️ **`cap === null` means no ceiling is configured, and then NOTHING is at one.** A
     * deployment turns the cap off with `AGENT_DAILY_TOKENS=0`; treating that as a ceiling of zero
     * would put every workspace that asked a single question into the tile.
     */
    const { cap } = usage.data;
    const atCeiling =
      cap === null || cap === 0
        ? []
        : usage.data.rows
            .filter((row) => row.inputTokens + row.outputTokens >= cap)
            .map((row) => ({ label: row.workspaceName }));

    return {
      kind: 'ready',
      health: {
        overdue: ops.data.chores
          .filter((chore) => chore.overdue)
          .map((chore) => ({
            label: chore.description,
            /* ⚠️ Cadences gone by since the last start, minus the one that is merely due now.
               Never run at all has no count, rather than an invented one. */
            ...(chore.lastStartedAt === null
              ? {}
              : {
                  missed: Math.max(
                    1,
                    Math.floor(
                      (Date.now() - new Date(chore.lastStartedAt).getTime()) / chore.everyMs,
                    ) - 1,
                  ),
                }),
          })),
        /* ⚠️ Per KIND, which is what the queue reports. A kind with forty failures is one thing to
           go and look at rather than forty, and the operations screen lists it that way too. */
        failedJobs: ops.data.jobs
          .filter((job) => job.failed > 0)
          .map((job) => ({ label: job.kind, count: job.failed })),
        atCeiling,
        suspended: orgs.data.data
          .filter((tenant) => tenant.status === 'suspended')
          .map((tenant) => ({ label: tenant.slug })),
        unenrolledOperators,
      },
    };
  }, [session]);

  useEffect(() => {
    let alive = true;
    void load()
      .catch((): HealthState => ({ kind: 'failed' }))
      .then((next) => {
        /* ⚠️ The guard is the screen unmounting mid-read, which React warns about and which here
           would also overwrite a newer answer with an older one. */
        if (alive) setState(next);
      });
    return () => {
      alive = false;
    };
  }, [load]);

  return state;
}
